/**
 * BranchFormSheet
 * Sheet deslizable para crear o editar una sucursal.
 *  - Modo crear  → branch prop omitido / null
 *  - Modo editar → branch prop con la sucursal existente
 *
 * En modo editar incluye una zona de estado para dar de baja o reactivar.
 * El estilo sigue el mismo patrón que ManagerFormSheet.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Store,
  Save,
  X,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import {
  type Branch,
  createBranch,
  updateBranch,
} from "@/services/branchesService";
import { type Company } from "@/services/companiesService";
import BranchInfraStatus from "@/components/BranchInfraStatus";

interface BranchFormSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Sucursal a editar. Omitir o pasar null para modo creación. */
  branch?: Branch | null;
  /** Lista de empresas disponibles (para el selector en modo creación). */
  companies: Company[];
  onSave: (branch: Branch) => void;
}

export default function BranchFormSheet({
  isOpen,
  onClose,
  branch,
  companies,
  onSave,
}: BranchFormSheetProps) {
  const isEditing = !!branch;

  const [companyId, setCompanyId]   = useState("");
  const [name, setName]             = useState("");
  const [address, setAddress]       = useState("");
  const [isSaving, setIsSaving]     = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  // Sync campos cuando cambia la sucursal o se abre el panel
  useEffect(() => {
    if (isOpen) {
      setCompanyId(branch?.company_id ?? "");
      setName(branch?.name ?? "");
      setAddress(branch?.address ?? "");
    }
  }, [isOpen, branch]);

  const handleClose = () => {
    if (isSaving || isToggling) return;
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("El nombre de la sucursal es obligatorio");
      return;
    }
    if (!isEditing && !companyId) {
      toast.error("Debes seleccionar una empresa");
      return;
    }

    setIsSaving(true);
    try {
      let saved: Branch;
      if (isEditing) {
        const payload: { name?: string; address?: string } = {};
        if (name.trim() !== branch.name) payload.name = name.trim();
        payload.address = address.trim() || undefined;

        if (Object.keys(payload).length === 0) {
          toast.info("No hay cambios que guardar");
          onClose();
          return;
        }

        saved = await updateBranch(branch.id, payload);
        toast.success("Sucursal actualizada correctamente");
      } else {
        saved = await createBranch({
          name: name.trim(),
          company_id: companyId,
          ...(address.trim() ? { address: address.trim() } : {}),
        });
        toast.success("Sucursal creada — aprovisionando entorno en segundo plano…");
      }
      onSave(saved);
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al guardar la sucursal"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!branch) return;
    const newStatus = !branch.is_active;
    setIsToggling(true);
    try {
      const updated = await updateBranch(branch.id, { is_active: newStatus });
      toast.success(
        newStatus
          ? "Sucursal reactivada correctamente"
          : "Sucursal dada de baja correctamente"
      );
      onSave(updated);
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al cambiar el estado"
      );
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-slate-900 border-white/10 text-white p-0 flex flex-col h-[100dvh] overflow-hidden"
      >
        {/* Header */}
        <SheetHeader
          className="flex-shrink-0 px-6 pb-4 border-b border-white/10"
          style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))" }}
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white flex-shrink-0">
              <Store className="h-7 w-7" />
            </div>
            <div className="min-w-0">
              <SheetTitle className="text-white text-lg font-semibold leading-tight">
                {isEditing ? "Editar sucursal" : "Nueva sucursal"}
              </SheetTitle>
              {isEditing && (
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <p className="text-sm text-white/50">{branch.company_name}</p>
                  <Badge
                    className={
                      branch.is_active
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                        : "bg-red-500/20 text-red-400 border-red-500/30"
                    }
                  >
                    {branch.is_active ? "Activa" : "Inactiva"}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Cuerpo con scroll */}
        <ScrollArea className="flex-1 min-h-0 px-6 py-5">
          <div className="space-y-6">

            {/* Información principal */}
            <section className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                Información
              </p>

              {/* Empresa — selector en creación */}
              {!isEditing && (
                <div className="space-y-2">
                  <Label htmlFor="branch-company" className="text-white/70 text-sm">
                    Empresa <span className="text-red-400">*</span>
                  </Label>
                  <Select
                    value={companyId}
                    onValueChange={setCompanyId}
                    disabled={isSaving || isToggling}
                  >
                    <SelectTrigger
                      id="branch-company"
                      className="bg-white/5 border-white/20 text-white focus:ring-0 focus:border-white/40"
                    >
                      <SelectValue placeholder="Seleccionar empresa..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-white/10 text-white">
                      {companies
                        .filter((c) => c.is_active)
                        .map((c) => (
                          <SelectItem
                            key={c.id}
                            value={c.id}
                            className="focus:bg-white/10 focus:text-white"
                          >
                            {c.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Nombre */}
              <div className="space-y-2">
                <Label htmlFor="branch-name" className="text-white/70 text-sm">
                  Nombre <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="branch-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Sucursal Norte"
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                             focus:bg-white/10 focus:border-white/40"
                  disabled={isSaving || isToggling}
                  autoFocus={!isEditing}
                />
              </div>

              {/* Dirección */}
              <div className="space-y-2">
                <Label htmlFor="branch-address" className="text-white/70 text-sm">
                  Dirección{" "}
                  <span className="text-white/30 text-xs">(opcional)</span>
                </Label>
                <Input
                  id="branch-address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Ej: Av. Reforma 123, Col. Centro"
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                             focus:bg-white/10 focus:border-white/40"
                  disabled={isSaving || isToggling}
                />
              </div>

              {/* URL backend — solo lectura, solo en edición si está disponible */}
              {isEditing && branch.backend_url && (
                <div className="space-y-1">
                  <p className="text-xs text-white/40">URL del backend</p>
                  <p
                    className="text-[11px] text-white/50 font-mono break-all"
                    title={branch.backend_url}
                  >
                    {branch.backend_url}
                  </p>
                </div>
              )}
            </section>

            {/* Estado de la sucursal — solo en edición */}
            {isEditing && (
              <>
                <Separator className="bg-white/10" />

                {/* Infraestructura en tiempo real */}
                <BranchInfraStatus branchId={branch.id} />

                <Separator className="bg-white/10" />

                <section className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                    Estado de la sucursal
                  </p>

                  <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      {branch.is_active ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                      )}
                      <p className="text-sm text-white/70">
                        {branch.is_active
                          ? "La sucursal está activa y operativa."
                          : "La sucursal está dada de baja y no puede operar."}
                      </p>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleToggleActive}
                      disabled={isToggling || isSaving}
                      className={
                        branch.is_active
                          ? "w-full border-red-500/40 text-red-400 hover:bg-red-500/10 hover:border-red-500/60 bg-transparent"
                          : "w-full border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500/60 bg-transparent"
                      }
                    >
                      {isToggling ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : branch.is_active ? (
                        <AlertTriangle className="h-4 w-4 mr-2" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                      )}
                      {branch.is_active ? "Dar de baja" : "Reactivar sucursal"}
                    </Button>
                  </div>
                </section>
              </>
            )}

          </div>
        </ScrollArea>

        {/* Footer fijo */}
        <SheetFooter
          className="flex-shrink-0 flex flex-row justify-center sm:justify-end gap-2 px-6 py-4
                     border-t border-white/10 bg-slate-900"
        >
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSaving || isToggling}
            className="border-white/20 text-white/70 hover:bg-white/10 hover:text-white bg-transparent"
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isToggling}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {isEditing ? "Guardar cambios" : "Crear sucursal"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
